import * as THREE from 'three';

// Shared central safe-zone attenuation (see the framing addendum).
//
// The album cover + title + artist are a permanent DOM overlay at the centre
// of the screen, and they are the subject of the image. Anything drawn in 3D
// behind them is ATTENUATED, never culled: the point is that you keep seeing
// the animation live behind the cover, it just stops competing with it.
//
// Evaluated in the VERTEX shader (cheap: a handful of ops per vertex, versus
// per-pixel over a full-screen additive layer) and handed to the fragment
// shader as a varying that multiplies final brightness.
//
// Every material shares ONE set of uniform holder objects - Three.js is happy
// for several materials to point at the same holders, so scene-manager only
// has to write these once per resize, not once per material and never per
// frame.

// Geometry of the zone, expressed in "height units": ndc.y spans [-1, 1]
// across the canvas height H, so 1.0 unit == H/2 px and a half-size of
// 0.22*H becomes 0.44 here. x is scaled by aspect in the shader so both axes
// share that same scale.
const SAFE_HALF_WIDTH = 0.22 * 2;
const SAFE_HALF_HEIGHT = 0.30 * 2;
const SAFE_FEATHER = 0.10 * 2;
// The artwork image sits slightly ABOVE true screen centre, because the
// title/artist text below it pulls the overlay block's own midpoint down.
// Measured: 46.2px above centre at 1080p, i.e. 0.0855 in these units.
const SAFE_CENTER_Y = 0.0855;
// Floor of the attenuation ramp - how much of a fragment survives dead
// centre behind the cover. 0.32 keeps motion legible without competing.
const SAFE_MIN_ATTENUATION = 0.32;

// Shared holder objects. Spread into a ShaderMaterial's `uniforms` with
// Object.assign - every material then reads the same live values.
//
// R19 (CLAUDE.md): TWO DISTINCT NOTIONS, NEVER CONFUSED.
//   uSafeCenter / uSafeHalfSize / uSafeFeather - WHERE THE COVER IS. Centre or
//     one of the four corners (the "Position de la pochette" setting). Only
//     for what concerns the cover itself and follows it: the attenuation mask
//     below, the cover's halo, shadow, reflection, a 3D plane aligned on it.
//   uStageCenter / uStageHalfSize - THE CENTRE OF COMPOSITION. Always the
//     centre of the screen: (0.5, 0.5) in normalised screen coordinates, i.e.
//     (0, 0) in the height units used here. uStageHalfSize is the size the
//     cover block has WHEN IT IS CENTRED (measured while it is, remembered or
//     estimated while it is not), so a shape drawn "around the cover" keeps
//     its size and its place when the cover moves to a corner. Orbits,
//     vanishing points, centres of rotation/zoom/warp, central holes,
//     auto-framing, objects placed "under the cover", "never in front of the
//     cover" constraints: all of it reads the stage, never the cover.
// With the cover centred the two coincide, so a visualizer moved from the
// one to the other renders exactly as before.
export const safeZoneUniforms = {
  uAspect: { value: 1 },
  uSafeCenter: { value: new THREE.Vector2(0, SAFE_CENTER_Y) },
  uSafeHalfSize: { value: new THREE.Vector2(SAFE_HALF_WIDTH, SAFE_HALF_HEIGHT) },
  uSafeFeather: { value: SAFE_FEATHER },
  uStageCenter: { value: new THREE.Vector2(0, 0) },
  uStageHalfSize: { value: new THREE.Vector2(SAFE_HALF_WIDTH, SAFE_HALF_HEIGHT) }
};

// The same holders under their own name, for code that only wants the stage.
export const stageUniforms = {
  uAspect: safeZoneUniforms.uAspect,
  uStageCenter: safeZoneUniforms.uStageCenter,
  uStageHalfSize: safeZoneUniforms.uStageHalfSize
};

// The stage centre in NDC (x and y in [-1, 1]), for code that works in clip
// space rather than height units.
export function stageCenterNdc(out) {
  const c = safeZoneUniforms.uStageCenter.value, a = safeZoneUniforms.uAspect.value || 1;
  const o = out || new THREE.Vector2();
  return o.set(c.x / a, c.y);
}

// The stage centre in WORLD coordinates for a 3D visualizer: the point on the
// camera ray through the stage centre at `distance` in front of the camera
// (view-space depth). Computed from the camera, so call it after the camera is
// posed (and after camera.updateMatrixWorld()).
const _ndcTmp = new THREE.Vector3();
export function stageWorldAt(camera, distance, out) {
  const n = stageCenterNdc();
  const o = out || new THREE.Vector3();
  _ndcTmp.set(n.x, n.y, 0.5).unproject(camera);
  camera.getWorldPosition(o);
  _ndcTmp.sub(o).normalize();
  const fwd = new THREE.Vector3();
  camera.getWorldDirection(fwd);
  const t = distance / Math.max(1e-6, _ndcTmp.dot(fwd));
  return o.addScaledVector(_ndcTmp, t);
}

// Called from scene-manager.js's resize handler AND on a live cadence (the
// director bus recomputes it - see director.js) since the overlay's own
// size can change from text content alone (a longer title wraps to a
// second line) without a window resize ever firing.
//
// overlayRectPx: the real #overlay element's getBoundingClientRect() - the
// CENTER and HALF-SIZE below are now MEASURED from it instead of the fixed
// SAFE_HALF_WIDTH/HEIGHT/CENTER_Y constants above, which stay only as the
// fallback for the one frame before any measurement has happened yet.
// Converted from DOM CSS pixels (origin top-left, y grows down) into the
// same "height units" (frame height 2, y grows up) the fixed constants
// were already expressed in - a drop-in upgrade from assumed to measured,
// not a change of unit system. uSafeFeather is a stylistic softness
// constant, not something meaningful to measure, and stays fixed.
//
// stageHalfPx (optional): { w, h } in CSS pixels, the half-size of the cover
// block AS IF CENTRED (see scene-manager's _measureSafeZone). Absent, the
// stage keeps its previous half-size. The stage centre never moves.
export function updateSafeZoneUniforms(width, height, overlayRectPx, stageHalfPx) {
  safeZoneUniforms.uAspect.value = height > 0 ? width / height : 1;
  if (stageHalfPx && height > 0) safeZoneUniforms.uStageHalfSize.value.set(stageHalfPx.w * 2 / height, stageHalfPx.h * 2 / height);
  if (!overlayRectPx || height <= 0) return;
  const scale = 2 / height;
  const rectCenterXPx = overlayRectPx.left + overlayRectPx.width / 2;
  const rectCenterYPx = overlayRectPx.top + overlayRectPx.height / 2;
  safeZoneUniforms.uSafeCenter.value.set(
    (rectCenterXPx - width / 2) * scale,
    (height / 2 - rectCenterYPx) * scale
  );
  safeZoneUniforms.uSafeHalfSize.value.set(
    (overlayRectPx.width / 2) * scale,
    (overlayRectPx.height / 2) * scale
  );
}

// GLSL: uniform declarations + the attenuation function. Prepend to a vertex
// shader, then call safeZoneAttenuation(clipPos) with the same clip-space
// position that goes into gl_Position, and pass the result out as a varying.
export const SAFE_ZONE_VERTEX_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  // Rounded-box signed distance in screen space, turned into a brightness
  // multiplier: SAFE_MIN_ATTENUATION inside the cover's rectangle, ramping
  // back to 1.0 over uSafeFeather outside it.
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_MIN_ATTENUATION}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
